import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ToasterSeverities } from "@core/enums/toaster-severities";
import { DialogHelper } from "@core/helpers/dialog-helper";
import { GroupModel } from "@core/models/groups.model";
import { GroupsService } from "@core/services/groups.service";
import { NotificationService } from "@core/services/notification.service";
import * as groupsActions from "@features/groups/store/groups.actions";
import {
  SET_GROUPS_LOADING,
  SET_RIGHTS_LOADING,
} from "@features/groups/store/groups.actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { catchError, mergeMap, Observable, of, switchMap } from "rxjs";

@Injectable()
export class GroupsEffects {
  loadGroupsList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(groupsActions.LOAD_GROUPS),
      mergeMap(() => {
        this.store.dispatch(SET_GROUPS_LOADING({ loading: true }));

        return this.groupsService.getGroups$().pipe(
          switchMap((groupsList) => {
            this.store.dispatch(SET_GROUPS_LOADING({ loading: false }));
            return of(groupsActions.SET_GROUPS_LIST({ groupsList }));
          }),
          catchError((error: HttpErrorResponse) => {
            this.store.dispatch(SET_GROUPS_LOADING({ loading: false }));
            return of(groupsActions.GROUPS_ACTION_FAILURE({ error }));
          })
        );
      })
    );
  });

  loadRightsList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(groupsActions.LOAD_RIGHTS),
      mergeMap(() => {
        this.store.dispatch(SET_RIGHTS_LOADING({ loading: true }));

        return this.groupsService.getRights$().pipe(
          switchMap((rightsList) => {
            this.store.dispatch(SET_RIGHTS_LOADING({ loading: false }));
            return of(groupsActions.SET_RIGHTS_LIST({ rightsList }));
          }),
          catchError((error: HttpErrorResponse) => {
            this.store.dispatch(SET_RIGHTS_LOADING({ loading: false }));
            return of(groupsActions.RIGHTS_ACTION_FAILURE({ error }));
          })
        );
      })
    );
  });

  saveGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(groupsActions.SAVE_GROUP),
      mergeMap(
        (action: {
          data: Pick<GroupModel, "description" | "name"> & { rights: string[] };
        }): Observable<Action> => {
          this.store.dispatch(SET_GROUPS_LOADING({ loading: true }));

          return this.groupsService.saveGroup$(action.data).pipe(
            switchMap(() => {
              this.dialog.closeAll();
              this.notificationService.show(
                ToasterSeverities.Success,
                "DATA_SAVED_SUCCESSFULLY"
              );

              this.store.dispatch(SET_GROUPS_LOADING({ loading: false }));
              return of(groupsActions.LOAD_GROUPS());
            }),
            catchError((response: HttpErrorResponse) => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_SAVED",
                response.error
              );
              this.store.dispatch(SET_GROUPS_LOADING({ loading: false }));
              return of(
                groupsActions.GROUPS_ACTION_FAILURE({ error: response?.error })
              );
            })
          );
        }
      )
    );
  });

  updateGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(groupsActions.UPDATE_GROUP),
      mergeMap(
        (action: {
          data: Pick<GroupModel, "description" | "name" | "id" | "version"> & {
            rights: string[];
          };
        }): Observable<Action> => {
          this.store.dispatch(SET_GROUPS_LOADING({ loading: true }));

          return this.groupsService.updateGroup$(action.data).pipe(
            switchMap(() => {
              this.dialog.closeAll();
              this.notificationService.show(
                ToasterSeverities.Success,
                "DATA_SAVED_SUCCESSFULLY"
              );

              this.store.dispatch(SET_GROUPS_LOADING({ loading: false }));
              return of(groupsActions.LOAD_GROUPS());
            }),
            catchError((response: HttpErrorResponse) => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "DATA_COULD_NOT_BE_SAVED",
                response.error
              );
              this.store.dispatch(SET_GROUPS_LOADING({ loading: false }));
              return of(
                groupsActions.GROUPS_ACTION_FAILURE({ error: response.error })
              );
            })
          );
        }
      )
    );
  });

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private groupsService: GroupsService,
    private store: Store,
    private dialog: DialogHelper
  ) {}
}
